import { HAND_DELIVERY_SERVICE } from "@erp/shared";
import { resendSchema, trackingSchema } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Ban,
  ChevronLeft,
  Download,
  ExternalLink,
  FileText,
  MoreHorizontal,
  PackageCheck,
  Pencil,
  Printer,
  RotateCw,
  ScanBarcode,
  Tag,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button, buttonVariants } from "~/components/ui/button";
import { Confirm } from "~/components/ui/confirm";
import { Menu } from "~/components/ui/menu";
import { Field } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import { Page, PageHeader, Panel, Stat } from "~/components/ui/page";
import { Sheet } from "~/components/ui/sheet";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { History } from "~/features/audit/history";
import { SHIPMENT_TONE } from "~/features/sales/status";
import { emptyParcel, ParcelFields, parcelPayload, useLabelCarriers, type ParcelDraft } from "~/features/shipments/parcel-fields";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { errorAt, schemaErrors, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { usePrint } from "~/lib/auto-print";
import { useCan } from "~/lib/session";
import { SerialPicker, useSerialTracking } from "~/features/stock/serial-input";
import { HelpTip } from "~/components/ui/help-tip";
import { helpHint } from "~/features/help/hint";

export const Route = createFileRoute("/_app/shipments/$id")({ component: ShipmentPage });

const fetchShipment = (id: string) => unwrap(api.shipments({ id }).get());
type Shipment = Awaited<ReturnType<typeof fetchShipment>>;

function BackLink() {
  const t = useT();
  return (
    <Link to="/shipments" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("shipments.title")}
    </Link>
  );
}

function ShipmentPage() {
  const { id } = Route.useParams();
  const query = useQuery({ queryKey: ["shipments", id], queryFn: () => fetchShipment(id) });
  if (query.isPending)
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-60" />
        <Skeleton className="mt-6 h-96 w-full" />
      </Page>
    );
  if (query.isError)
    return (
      <Page>
        <BackLink />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  return <ShipmentView shipment={query.data} />;
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="border-b border-line px-5 py-5 last:border-b-0 md:px-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-medium">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function ShipmentView({ shipment }: { shipment: Shipment }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const printer = usePrint();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmingSerials, setConfirmingSerials] = useState(false);
  const serialTracking = useSerialTracking();
  const canWrite = can("stock:write") && shipment.status !== "cancelled";
  const trackedLines = serialTracking ? shipment.lines.filter((l) => l.serialTracked && l.variantId) : [];
  const serialsEditable =
    canWrite &&
    trackedLines.length > 0 &&
    shipment.kind !== "return" &&
    (shipment.status === "shipped" || shipment.status === "delivered");
  const resendable = canWrite && shipment.kind !== "return" && (shipment.status === "shipped" || shipment.status === "delivered");
  // A parcel known to Shopify is cancelled there first: the API refuses it, the menu still offers it so the reason shows.
  const cancellable = canWrite && shipment.kind !== "return" && shipment.status === "shipped";
  const [cancelling, setCancelling] = useState(false);
  const [delivering, setDelivering] = useState(false);

  const refresh = () => {
    for (const key of ["shipments", "orders", "sav"]) void queryClient.invalidateQueries({ queryKey: [key] });
  };
  const delivered = useMutation({
    mutationFn: () => unwrap(api.shipments({ id: shipment.id }).delivered.post()),
    onSuccess: () => {
      refresh();
      setDelivering(false);
      toast.success(t("shipments.toasts.delivered"));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(api.shipments({ id: shipment.id }).cancel.post()),
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      setCancelling(false);
      toast.success(t("shipments.cancel.done", { number: shipment.number }));
    },
    onError: (error) => {
      setCancelling(false);
      toast.error(t(errorMessageKey(error)));
    },
  });

  return (
    <Page>
      <PageHeader
        back={<BackLink />}
        title={<span className="tabular">{shipment.number}</span>}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={SHIPMENT_TONE[shipment.status]}>{t(`shipments.statuses.${shipment.status}`)}</Badge>
            <span>{t(`shipments.kinds.${shipment.kind}`)}</span>
          </span>
        }
        actions={
          <>
            <a
              href={`/api/shipments/${shipment.id}/delivery-note`}
              target="_blank"
              rel="noreferrer"
              className={buttonVariants({ variant: "secondary" })}
            >
              <FileText />
              <span className="max-sm:sr-only">{t("shipments.actions.deliveryNote")}</span>
            </a>
            {canWrite && shipment.kind !== "return" ? (
              <span className="inline-flex items-center gap-0.5">
                <a href={`/api/shipments/${shipment.id}/laposte`} download className={buttonVariants({ variant: "secondary" })}>
                  <Download />
                  <span className="max-sm:sr-only">{t("parcels.laposteFile")}</span>
                </a>
                <HelpTip id="shipment.parcelLaposteFile" />
              </span>
            ) : null}
            {shipment.labelKey ? (
              <a
                href={`/api/shipments/${shipment.id}/label`}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: "secondary" })}
              >
                <Tag />
                <span className="max-sm:sr-only">{t("shipments.actions.label")}</span>
              </a>
            ) : null}
            {shipment.labelKey ? (
              <Button
                variant="secondary"
                loading={printer.pending}
                onClick={() => printer.print("label", shipment.id, `/api/shipments/${shipment.id}/label`)}
              >
                <Printer />
                <span className="max-sm:sr-only">{t("parcels.printLabel")}</span>
              </Button>
            ) : null}
            {resendable ? (
              <span className="inline-flex items-center gap-0.5">
                <Button onClick={() => setResending(true)}>
                  <RotateCw />
                  <span className="max-sm:sr-only">{t("shipments.actions.resend")}</span>
                </Button>
                <HelpTip id="shipment.resend" />
              </span>
            ) : null}
            {canWrite && shipment.status === "shipped" ? (
              <span className="inline-flex items-center gap-0.5">
                <Button variant="primary" onClick={() => setDelivering(true)}>
                  <PackageCheck />
                  {t("shipments.actions.delivered")}
                </Button>
                <HelpTip id="shipment.delivered" />
              </span>
            ) : null}
            {cancellable ? (
              <Menu
                trigger={
                  <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                    <MoreHorizontal />
                  </Button>
                }
                actions={[
                  {
                    label: t("shipments.cancel.action"),
                    hint: helpHint(t, "shipment.cancel"),
                    icon: <Ban />,
                    tone: "danger",
                    onSelect: () => setCancelling(true),
                  },
                ]}
              />
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-5">
          <Panel>
            <Section title={t("shipments.sections.lines")}>
              <ul className="-mx-5 flex flex-col md:-mx-6">
                {shipment.lines.map((l) => (
                  <li
                    key={l.id}
                    className="flex items-center justify-between gap-4 border-t border-line px-5 py-2.5 first:border-t-0 md:px-6"
                  >
                    <div className="min-w-0">
                      <p className="text-sm">{l.label}</p>
                      {l.sku ? <p className="text-xs text-fg-3 tabular">{l.sku}</p> : null}
                      {l.serials.length ? <p className="text-xs text-fg-2 tabular">{l.serials.join(", ")}</p> : null}
                    </div>
                    <span className="shrink-0 text-sm tabular">{format.number(l.quantity)}</span>
                  </li>
                ))}
              </ul>
              {shipment.parcels.length ? (
                <ul className="mt-3 flex flex-col gap-1 rounded-control bg-sunken px-3 py-2 text-sm text-fg-2">
                  {shipment.parcels.map((p, i) => (
                    <li key={i} className="flex flex-wrap justify-between gap-2 tabular">
                      <span>
                        {p.heightCm == null
                          ? t("parcels.summaryEnvelope", { n: i + 1, length: p.lengthCm, width: p.widthCm })
                          : t("parcels.summary", { n: i + 1, length: p.lengthCm, width: p.widthCm, height: p.heightCm })}
                      </span>
                      <span>
                        {t("parcels.weight", { grams: format.number(p.weightGrams) })}
                        {p.trackingNumber ? ` — ${p.trackingNumber}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {shipment.serialsPending ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-control bg-warning-soft px-3 py-2 text-sm text-warning">
                  <span>
                    <span className="block font-medium">{t("stock.serials.pending")}</span>
                    <span className="block text-xs">{t("stock.serials.pendingHint")}</span>
                  </span>
                  {serialsEditable ? (
                    <span className="inline-flex items-center gap-0.5">
                      <Button size="sm" onClick={() => setConfirmingSerials(true)}>
                        {t("stock.serials.confirm")}
                      </Button>
                      <HelpTip id="shipment.serials" />
                    </span>
                  ) : null}
                </div>
              ) : serialsEditable ? (
                <span className="mt-3 inline-flex items-center gap-0.5">
                  <Button size="sm" variant="ghost" onClick={() => setConfirmingSerials(true)}>
                    <ScanBarcode />
                    {t("stock.serials.confirm")}
                  </Button>
                  <HelpTip id="shipment.serials" />
                </span>
              ) : null}
              {shipment.note ? (
                <p className="mt-3 rounded-control bg-sunken px-3 py-2 text-sm whitespace-pre-line text-fg-2">{shipment.note}</p>
              ) : null}
            </Section>
            <Section
              title={t("shipments.sections.tracking")}
              action={
                canWrite ? (
                  <span className="inline-flex items-center gap-0.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                      <Pencil />
                      {shipment.trackingNumber ? t("shipments.actions.editTracking") : t("shipments.actions.setTracking")}
                    </Button>
                    <HelpTip id="shipment.tracking" />
                  </span>
                ) : null
              }
            >
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat
                  label={t("shipments.fields.carrier")}
                  value={
                    shipment.service === HAND_DELIVERY_SERVICE
                      ? t("shipments.handDelivery")
                      : t(`shipments.carriers.${shipment.carrier}`)
                  }
                />
                <Stat
                  label={t("shipments.fields.weight")}
                  value={shipment.weightGrams ? format.number(shipment.weightGrams) : "—"}
                />
                <Stat
                  label={t("shipments.fields.shippedAt")}
                  value={shipment.shippedAt ? format.date(shipment.shippedAt, "short") : "—"}
                />
                <Stat
                  label={t("shipments.fields.deliveredAt")}
                  value={shipment.deliveredAt ? format.date(shipment.deliveredAt, "short") : "—"}
                />
              </dl>
              <div className="mt-4">
                {shipment.trackingNumber ? (
                  <p className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-medium tabular">{shipment.trackingNumber}</span>
                    {shipment.trackingUrl ? (
                      <a
                        href={shipment.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-h-11 items-center gap-1 text-fg-2 underline-offset-4 hover:text-fg hover:underline md:min-h-0"
                      >
                        {t("shipments.actions.track")}
                        <ExternalLink className="size-3.5" aria-hidden />
                      </a>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-sm text-fg-3">{t("shipments.hints.noTracking")}</p>
                )}
              </div>
            </Section>
          </Panel>
        </div>

        <aside className="flex flex-col gap-5">
          <Panel>
            <Section title={t("shipments.sections.recipient")}>
              <p className="font-medium">{shipment.recipient.name}</p>
              <address className="mt-1 text-sm not-italic leading-relaxed text-fg-2">
                {format.address(shipment.address).map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </address>
              {shipment.recipient.phone ? <p className="mt-1 text-sm text-fg-2 tabular">{shipment.recipient.phone}</p> : null}
              {shipment.recipient.email ? <p className="text-sm text-fg-2">{shipment.recipient.email}</p> : null}
              <div className="mt-3 flex flex-col items-start">
                {shipment.order ? (
                  <Link
                    to="/orders/$id"
                    params={{ id: shipment.order.id }}
                    className="inline-flex min-h-11 items-center text-sm text-fg-2 underline-offset-4 hover:text-fg hover:underline md:min-h-8"
                  >
                    {t("shipments.fields.order")} <span className="ml-1 tabular">{shipment.order.number}</span>
                  </Link>
                ) : null}
                {shipment.savTicketId ? (
                  <Link
                    to="/sav/$id"
                    params={{ id: shipment.savTicketId }}
                    className="inline-flex min-h-11 items-center text-sm text-fg-2 underline-offset-4 hover:text-fg hover:underline md:min-h-8"
                  >
                    {t("shipments.actions.openSav")}
                  </Link>
                ) : null}
              </div>
            </Section>
          </Panel>
          <Panel>
            <Section title={t("shipments.sections.activity")}>
              <History entityType="shipment" entityId={shipment.id} />
            </Section>
          </Panel>
        </aside>
      </div>

      <Confirm
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("shipments.cancel.title", { number: shipment.number })}
        body={
          <span className="flex flex-col gap-1.5">
            <span>{t("shipments.cancel.stock")}</span>
            {shipment.lines.some((l) => l.serials.length) ? <span>{t("shipments.cancel.serials")}</span> : null}
            {shipment.kind === "outbound" && shipment.order ? (
              <span>{t("shipments.cancel.order", { number: shipment.order.number })}</span>
            ) : null}
            {shipment.trackingNumber || shipment.labelKey ? <span>{t("shipments.cancel.label")}</span> : null}
          </span>
        }
        confirmLabel={t("shipments.cancel.action")}
        tone="danger"
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
      <Confirm
        open={delivering}
        onOpenChange={setDelivering}
        title={t("confirms.shipmentDelivered.title")}
        body={t("confirms.shipmentDelivered.body")}
        confirmLabel={t("confirms.shipmentDelivered.confirm")}
        loading={delivered.isPending}
        onConfirm={() => delivered.mutate()}
      />

      <ResendSheet open={resending} onOpenChange={setResending} shipment={shipment} />

      {serialsEditable ? (
        <SerialsSheet open={confirmingSerials} onOpenChange={setConfirmingSerials} shipment={shipment} lines={trackedLines} />
      ) : null}
      <TrackingSheet
        open={editing}
        onOpenChange={setEditing}
        shipment={shipment}
        onSaved={() => {
          refresh();
          setEditing(false);
        }}
      />
    </Page>
  );
}

function TrackingSheet({
  open,
  onOpenChange,
  shipment,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: Shipment;
  onSaved: () => void;
}) {
  const t = useT();
  const [value, setValue] = useState(shipment.trackingNumber ?? "");
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});
  const payload = { trackingNumber: value };
  const errors = { ...serverErrors, ...(submitted ? schemaErrors(trackingSchema, payload) : {}) };

  const save = useMutation({
    mutationFn: () => unwrap(api.shipments({ id: shipment.id }).tracking.put(trackingSchema.parse(payload))),
    onSuccess: () => {
      toast.success(t("shipments.toasts.tracking"));
      onSaved();
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = () => {
    setSubmitted(true);
    setServerErrors({});
    if (Object.keys(schemaErrors(trackingSchema, payload)).length) return;
    save.mutate();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("shipments.trackingTitle")}
      description={shipment.number}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>
            {t("common.actions.save")}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label={t("shipments.fields.tracking")} error={errorAt(t, errors, "trackingNumber")}>
          <Input value={value} onChange={(e) => setValue(e.target.value)} autoComplete="off" className="tabular" />
        </Field>
      </form>
    </Sheet>
  );
}

/** The units of a parcel prepared outside the ERP, or to correct: what left goes back, what is picked leaves. */
function SerialsSheet({
  open,
  onOpenChange,
  shipment,
  lines,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: Shipment;
  lines: Shipment["lines"];
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [serials, setSerials] = useState<Record<string, string[]>>(() => Object.fromEntries(lines.map((l) => [l.id, l.serials])));
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        api
          .shipments({ id: shipment.id })
          .serials.put({ lines: lines.map((l) => ({ lineId: l.id, serials: serials[l.id] ?? [] })) }),
      ),
    onSuccess: () => {
      for (const key of ["shipments", "stock"]) void queryClient.invalidateQueries({ queryKey: [key] });
      toast.success(t("stock.serials.confirmed"));
      onOpenChange(false);
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("stock.serials.confirm")}
      description={t("stock.serials.pickHint")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" onClick={() => confirm.mutate()} loading={confirm.isPending}>
            <ScanBarcode />
            {t("stock.serials.confirm")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {lines.map((l) => (
          <div key={l.id}>
            <p className="mb-2 text-sm font-medium">
              {l.label} <span className="text-fg-3 tabular">× {l.quantity}</span>
            </p>
            <SerialPicker
              variantId={l.variantId!}
              value={serials[l.id] ?? []}
              onChange={(next) => setSerials((s) => ({ ...s, [l.id]: next }))}
              expected={l.quantity}
              extra={l.serials}
            />
          </div>
        ))}
      </div>
    </Sheet>
  );
}

/** Same content to the same address (lost or damaged parcel): a new BL, label and stock out. */
function ResendSheet({
  open,
  onOpenChange,
  shipment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: Shipment;
}) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const carriers = useLabelCarriers();
  const [parcel, setParcel] = useState<ParcelDraft>(() => ({
    ...emptyParcel(),
    carrier: shipment.carrier,
    weight: String(shipment.weightGrams ?? 1000),
  }));
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});
  // The same articles leave again, as new physical units: tracked ones are picked afresh.
  const serialTracking = useSerialTracking();
  const trackedLines = serialTracking ? shipment.lines.filter((l) => l.serialTracked && l.variantId) : [];
  const [serials, setSerials] = useState<Record<string, string[]>>({});
  const payload = {
    ...parcelPayload(parcel, carriers),
    note,
    ...(trackedLines.length ? { lines: trackedLines.map((l) => ({ lineId: l.id, serials: serials[l.id] ?? [] })) } : {}),
  };
  const errors = { ...serverErrors, ...(submitted ? schemaErrors(resendSchema, payload) : {}) };

  const resend = useMutation({
    mutationFn: () => unwrap(api.shipments({ id: shipment.id }).resend.post(resendSchema.parse(payload))),
    onSuccess: (next) => {
      for (const key of ["shipments", "orders", "sav", "stock"]) void queryClient.invalidateQueries({ queryKey: [key] });
      toast.success(t("shipments.toasts.shipped", { number: next.number }));
      onOpenChange(false);
      void navigate({ to: "/shipments/$id", params: { id: next.id } });
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = () => {
    setSubmitted(true);
    setServerErrors({});
    if (Object.keys(schemaErrors(resendSchema, payload)).length) return;
    resend.mutate();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("shipments.resend.title")}
      description={t("shipments.resend.hint", { number: shipment.number })}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={resend.isPending}>
            <RotateCw />
            {t("shipments.actions.resend")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {trackedLines.map((l) => (
          <div key={l.id}>
            <p className="mb-2 text-sm font-medium">
              {l.label} <span className="text-fg-3 tabular">× {l.quantity}</span>
            </p>
            <SerialPicker
              variantId={l.variantId!}
              value={serials[l.id] ?? []}
              onChange={(next) => setSerials((s) => ({ ...s, [l.id]: next }))}
              expected={l.quantity}
            />
          </div>
        ))}
        <ParcelFields value={parcel} onChange={setParcel} carriers={carriers} errors={errors} />
        <Field label={t("shipments.fields.note")} optionalLabel={t("common.states.optional")}>
          <Textarea
            rows={3}
            value={note}
            placeholder={t("shipments.resend.notePlaceholder")}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </div>
    </Sheet>
  );
}
